import React, {
  useEffect,
  memo,
  useContext,
  useState,
  useCallback,
} from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { TOContext } from "context/TContext";
import { useApplyPaymentCardMutation } from "api";
import { useLocation } from "react-router-dom";
// import {PAYMENT_WITH_CARD} from "hooks/useCreditCard";
import { usePaymentContext } from "context/TPaymentContext";
import GoogleOrApplePaySkeleton from "components/Skeleton/GoogleOrApplePaySkeleton";
import { getForwardPaymentIntentId } from "helpers/getForwardPaymentIntentId";

const GooglePayButtonForward = ({
  handlePaymentRequest,
  handleCardError,
  purchaseModel,
  userInfo,
}) => {
  const {
    paymentIntent,
    isForwardGooglePaySupported,
    setForwardPaymentMethod,
    setIsApplePayOrGooglePay,
    isLoadingForWard,
  } = usePaymentContext();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const oId = searchParams.get("oId");
  const [buttonLoading, setButtonLoading] = useState(false);
  const [applyPaymentCard] = useApplyPaymentCardMutation();
  const { setIsPaid, charges } = useContext(TOContext);

  const handleSubmitGooglePay = useCallback(
    async (token, paymentData) => {
      try {
        const forwardPaymentIntentId = getForwardPaymentIntentId(oId);

        const requestData = {
          orderId: oId,
          forwardPaymentMethod: token,
          forwardPaymentIntentId: forwardPaymentIntentId,
          tipInfo: {
            kitchen: Number(charges?.tipInfo?.kitchen) || 0,
            driver: Number(charges?.tipInfo?.driver) || 0,
          },
        };

        localStorage.setItem(`${oId}-balanceDue`, charges.balanceDue);

        const response = await applyPaymentCard({ requestData });

        if (response?.error?.data) {
          setIsPaid(false);
          setIsApplePayOrGooglePay(false);
          handleCardError(true);
          const errorMessage =
            response?.error?.data?.message ||
            "Payment failed. Please try again.";
          toast.error(errorMessage);
        } else {
          setIsPaid(true);
        }
      } catch (error) {
        setIsPaid(false);
        setIsApplePayOrGooglePay(false);
        handleCardError(true);
        const errorMessage =
          error?.message ||
          error?.data?.message ||
          "Payment failed. Please try again.";
        toast.error(errorMessage);
        console.error("Error in handleSubmitGooglePay:", error);
      }
    },
    [
      oId,
      charges?.balanceDue,
      charges?.tipInfo?.kitchen,
      charges?.tipInfo?.driver,
      applyPaymentCard,
      setIsPaid,
      setIsApplePayOrGooglePay,
      handleCardError,
    ],
  );

  useEffect(() => {
    const mountGooglePay = async () => {
      if (window.Forward && isForwardGooglePaySupported) {
        const googlePayButtonContainer = document.getElementById(
          "google-pay-payment-button",
        );
        if (googlePayButtonContainer?.childNodes?.length === 0) {
          setButtonLoading(true);
        }
        const googlePayButton = await window?.Forward.createGooglePayButton({
          apiKey: process.env.REACT_APP_FORWARD_KEY,
          clientSecret: paymentIntent,
          merchantId: process.env.REACT_APP_FORWARD_GOOGLE_PAY_MERCHANT_ID,
        });
        if (typeof document !== "undefined") {
          const googlePayButtonContainer = document.getElementById(
            "google-pay-payment-button",
          );
          if (googlePayButtonContainer && !isLoadingForWard) {
            setButtonLoading(false);
            if (googlePayButtonContainer?.childNodes?.length > 0) {
              googlePayButtonContainer?.childNodes?.forEach((item) => {
                googlePayButtonContainer.removeChild(item);
              });
            }
          }
        }
        if (!isLoadingForWard) {
          console.log("Click");
          const unmountGooglePayButton = googlePayButton.mount(
            "#google-pay-payment-button",
            {
              onSuccess: async (data) => {
                setForwardPaymentMethod(data?.token);
                handleCardError(false);
                console.log(data, "data>>>>unmountGooglePayButton");
                setIsApplePayOrGooglePay(true);
                setTimeout(() => {
                  handleSubmitGooglePay(data?.token, data);
                }, 150);
              },
              onError: () => {
                handleCardError(true);
                setIsApplePayOrGooglePay(false);
              },
              onCancel: () => {
                console.log("User closed Google Pay pop-up window");
                setIsApplePayOrGooglePay(false);
              },
              googlePayButtonOptions: {
                buttonType: "checkout",
              },
            },
          );
          return () => unmountGooglePayButton();
        }
      }
    };
    mountGooglePay();
  }, [
    handleCardError,
    handlePaymentRequest,
    isForwardGooglePaySupported,
    isLoadingForWard,
    paymentIntent,
    purchaseModel,
    setForwardPaymentMethod,
    setIsApplePayOrGooglePay,
    handleSubmitGooglePay,
  ]);

  return (
    <Box sx={{ position: "relative" }}>
      <Box sx={{ height: "42px" }}>
        {(isLoadingForWard || buttonLoading) && <GoogleOrApplePaySkeleton />}
        <Box id="google-pay-payment-button" sx={{ flex: 1, height: "100%" }} />
      </Box>
    </Box>
  );
};

export default memo(GooglePayButtonForward);
